import type { SignalContext } from "@tracking-web/shared";

/**
 * Almacén en vivo de las señales de comportamiento. Los collectors escriben
 * aquí; el engine lee de aquí. Cualquier cambio marca el contexto como "sucio"
 * y dispara una re-evaluación debounced (la registra index.ts).
 */
export type Context = {
  state: SignalContext;
  /** marca un cambio y agenda una re-evaluación. */
  touch: () => void;
  /** registra el callback que evalúa las reglas (lo pone index.ts). */
  onChange: (cb: () => void) => void;
  /**
   * Reinicia las señales POR-PÁGINA para una navegación SPA (página virtual nueva).
   * Devuelve false si el pathname normalizado NO cambió (p. ej. solo cambió query o
   * hash: no es un page view) → el llamante no hace nada. Resetea lo que una página
   * fresca tendría a cero, INCLUIDO activeReadingMs (T2): así "tiempo activo en ESTA
   * página" es coherente en MPA y SPA, y los objetivos time_on_page de experimentos miden
   * por página. La métrica de tiempo de SESIÓN ya NO depende de activeReadingMs (vive en
   * sessionActiveMs, que NO se resetea). El llamante (index.ts) vuelca el tiempo activo de
   * la página saliente ANTES de llamar aquí. NO toca:
   *   - sessionActiveMs: acumulado de sesión (su MÁX es el total; resetearlo lo rompería).
   *   - visitCount / device / referrer: son de sesión, no de página.
   */
  resetForPage: (pathname: string) => boolean;
};

// Copia local de normalizePagePath (packages/shared/src/url.ts) para no
// arrastrar el paquete shared (y zod) al bundle. Si cambias uno, cambia el otro.
export function normalizePagePath(input: string): string {
  let path = (input || "").split(/[?#]/)[0];
  if (!path.startsWith("/")) path = "/" + path;
  if (path.length > 1) path = path.replace(/\/+$/, "");
  return path.toLowerCase() || "/";
}

function detectDevice(): SignalContext["device"] {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

/** Clave de sessionStorage del tiempo activo ACUMULADO de la sesión (encadena entre
 *  cargas de página en MPA; en SPA es una sola carga). idle.ts la mantiene al día. */
export const SESSION_ACTIVE_KEY = "tw_sa";
function readSessionActive(): number {
  try {
    const v = Number(sessionStorage.getItem(SESSION_ACTIVE_KEY));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

export function createContext(): Context {
  const state: SignalContext = {
    scrollDepthPct: 0,
    readingProgressPct: 0,
    timeOnPageMs: 0,
    activeReadingMs: 0,
    sessionActiveMs: readSessionActive(), // encadena con páginas/cargas anteriores de la sesión
    idleMs: 0,
    exitIntent: false,
    backExitIntent: false,
    visitCount: 1,
    device: detectDevice(),
    referrer: (() => {
      try {
        return document.referrer ? new URL(document.referrer).host : "";
      } catch {
        return "";
      }
    })(),
    quizCompleted: false,
    pageUrl: normalizePagePath(location.pathname),
    // País: de SESIÓN (no de página). Arranca "" y lo puebla boot() con la caché/red (ver geo.ts);
    // como device/referrer/visitCount, NO se resetea en resetForPage() → persiste en navegación SPA.
    country: "",
    clicks: {},
    hovers: {},
    sectionsViewed: {},
    quizAnswer: {},
  };

  let changeCb: (() => void) | null = null;
  let scheduled = false;

  // Re-evaluación debounced a un frame: muchas señales pueden cambiar a la vez
  // (scroll), pero solo evaluamos una vez por frame para no bloquear el hilo.
  function touch() {
    if (scheduled || !changeCb) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      changeCb?.();
    });
  }

  function resetForPage(pathname: string): boolean {
    const next = normalizePagePath(pathname);
    if (next === state.pageUrl) return false; // mismo pathname (query/hash) → no es page view
    state.pageUrl = next;
    // Señales de tiempo/scroll/lectura: a cero, como en una carga fresca.
    state.timeOnPageMs = 0;
    state.activeReadingMs = 0; // T2: tiempo activo POR-PÁGINA (el de sesión vive en sessionActiveMs)
    state.scrollDepthPct = 0;
    state.readingProgressPct = 0;
    state.idleMs = 0;
    state.exitIntent = false;
    state.backExitIntent = false;
    state.quizCompleted = false;
    // Señales indexadas (por selector/sección/quiz): nuevas en cada página.
    state.clicks = {};
    state.hovers = {};
    state.sectionsViewed = {};
    state.quizAnswer = {};
    // sessionActiveMs, visitCount, device, referrer, country: NO se tocan (de sesión; ver doc del tipo).
    return true;
  }

  return {
    state,
    touch,
    onChange: (cb) => {
      changeCb = cb;
    },
    resetForPage,
  };
}
